/**
 * [INPUT]: 依赖 React Router 当前路径、身份上下文与后台导航项（含公告控制面）
 * [OUTPUT]: 对外提供带高对比度导航状态、公告入口、顶部状态栏与响应式移动菜单的后台壳
 * [POS]: admin-web 结构层，包裹所有受保护页面，不承载领域查询；导航颜色只使用互斥设计令牌，避免 utility 顺序改变语义
 * [PROTOCOL]: 变更时更新此头部，然后检查 CLAUDE.md
 */
import { useState, type ReactNode } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import { Activity, BellRing, Bot, Calculator, ChevronRight, Gamepad2, Gift, LayoutDashboard, LogOut, Menu, PanelLeftClose, PanelLeftOpen, Rocket, ScrollText, Settings2, Ticket, Users, X } from 'lucide-react';
import { useAuth } from '../context/auth';
import { cn } from '../lib/utils';
import { Button, Kicker } from './ui';

const navigation = [
  { label: '总览', href: '/', icon: LayoutDashboard },
  { label: 'MVP 上架', href: '/publish', icon: Rocket },
  { label: '游戏目录', href: '/games', icon: Gamepad2 },
  { label: '公告系统', href: '/announcements', icon: BellRing },
  { label: '用户', href: '/users', icon: Users },
  { label: '灵感积分', href: '/inspiration', icon: Gift },
  { label: '兑换码', href: '/redeem-codes', icon: Ticket },
  { label: 'LLM 渠道', href: '/llm-channels', icon: Bot },
  { label: 'AI 计费', href: '/ai-pricing', icon: Calculator },
  { label: 'AI 用量', href: '/ai-usage', icon: Activity },
  { label: '审计日志', href: '/audit-logs', icon: ScrollText },
];
const systemNavigation = [{ label: '系统管理', href: '/settings', icon: Settings2 }] as const;
type NavigationEntry = (typeof navigation)[number] | (typeof systemNavigation)[number];

const pathNames: Record<string, string> = {
  '/': '控制面板',
  '/publish': 'MVP 上架',
  '/games': '游戏目录',
  '/announcements': '公告系统',
  '/users': '用户管理',
  '/inspiration': '灵感积分',
  '/redeem-codes': '兑换码',
  '/llm-channels': 'LLM 渠道',
  '/ai-pricing': 'AI 计费规则',
  '/ai-usage': 'AI 用量',
  '/audit-logs': '审计日志',
  '/settings': '系统管理',
};

export function AdminLayout({ children }: { children: ReactNode }) {
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const { session, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const currentName = location.pathname === '/games/new'
    ? '新建游戏'
    : location.pathname.startsWith('/games/')
      ? location.pathname.endsWith('/edit')
        ? '编辑游戏'
        : location.pathname.endsWith('/prompts')
          ? '提示词管理'
          : location.pathname.endsWith('/releases')
            ? '发布版本'
            : '游戏详情'
      : pathNames[location.pathname] ?? '控制面板';

  function handleLogout() {
    logout();
    navigate('/login');
  }

  return (
    <div className="min-h-screen bg-paper text-ink">
      <aside className={cn('fixed inset-y-0 left-0 z-40 hidden flex-col border-r border-white/10 bg-ink text-white transition-all duration-200 lg:flex', collapsed ? 'w-[76px]' : 'w-[248px]')}>
        <Brand collapsed={collapsed} />
        <nav className="flex-1 space-y-1 px-3 py-5">
          <Kicker>Workspace</Kicker>
          <div className="mt-3 space-y-1">
            {navigation.map((item) => <NavigationItem key={item.href} item={item} collapsed={collapsed} />)}
          </div>
          <Kicker>System</Kicker>
          <div className="mt-3 space-y-1">
            {systemNavigation.map((item) => <NavigationItem key={item.href} item={item} collapsed={collapsed} />)}
          </div>
        </nav>
        <div className="border-t border-white/10 p-3">
          <div className={cn('flex items-center gap-3 rounded-xl bg-white/[0.06] p-3', collapsed && 'justify-center p-2')}>
            <img src="/elseyou-brand.png" alt="" className="h-8 w-8 shrink-0 rounded-lg object-cover" />
            {!collapsed && <div className="min-w-0"><p className="truncate text-xs font-semibold">{session?.name ?? '管理员'}</p><p className="truncate text-[10px] text-white/45">{session?.email ?? '未登录'}</p></div>}
          </div>
          <button className={cn('mt-2 flex w-full items-center gap-3 rounded-lg px-3 py-2 text-xs text-white/45 transition hover:bg-white/10 hover:text-white', collapsed && 'justify-center px-2')} onClick={handleLogout} title={collapsed ? '退出登录' : undefined}><LogOut className="h-4 w-4" />{!collapsed && '退出登录'}</button>
        </div>
        <button className="absolute -right-3 top-20 hidden h-6 w-6 items-center justify-center rounded-full border border-line bg-white text-slate-500 shadow-sm lg:flex" onClick={() => setCollapsed((value) => !value)} aria-label={collapsed ? '展开侧栏' : '收起侧栏'}>{collapsed ? <PanelLeftOpen className="h-3.5 w-3.5" /> : <PanelLeftClose className="h-3.5 w-3.5" />}</button>
      </aside>

      {mobileOpen && <div className="fixed inset-0 z-40 bg-ink/40 lg:hidden" onClick={() => setMobileOpen(false)} />}
      <aside className={cn('fixed inset-y-0 left-0 z-50 flex w-[280px] flex-col bg-ink text-white transition-transform lg:hidden', mobileOpen ? 'translate-x-0' : '-translate-x-full')}>
        <div className="flex items-center justify-between px-5 py-5"><Brand /><button className="rounded-lg p-2 text-white/55 hover:bg-white/10 hover:text-white" onClick={() => setMobileOpen(false)} aria-label="关闭菜单"><X className="h-5 w-5" /></button></div>
        <nav className="flex-1 space-y-1 px-3 py-2"><Kicker>Workspace</Kicker>{navigation.map((item) => <NavigationItem key={item.href} item={item} onNavigate={() => setMobileOpen(false)} />)}<Kicker>System</Kicker>{systemNavigation.map((item) => <NavigationItem key={item.href} item={item} onNavigate={() => setMobileOpen(false)} />)}</nav>
        <div className="border-t border-white/10 p-4"><button className="flex w-full items-center gap-3 rounded-lg px-3 py-2 text-xs text-white/55 hover:bg-white/10 hover:text-white" onClick={handleLogout}><LogOut className="h-4 w-4" />退出登录</button></div>
      </aside>

      <div className={cn('transition-[padding] duration-200', collapsed ? 'lg:pl-[76px]' : 'lg:pl-[248px]')}>
        <header className="sticky top-0 z-30 flex h-[72px] items-center justify-between border-b border-line bg-paper/90 px-4 backdrop-blur-xl sm:px-6 lg:px-8">
          <div className="flex min-w-0 items-center gap-3"><button className="rounded-lg p-2 text-slate-500 hover:bg-slate-100 lg:hidden" onClick={() => setMobileOpen(true)} aria-label="打开菜单"><Menu className="h-5 w-5" /></button><div className="flex min-w-0 items-center gap-2 text-sm"><span className="hidden text-slate-400 sm:inline">Elseyou</span><ChevronRight className="hidden h-3.5 w-3.5 text-slate-300 sm:inline" /><span className="truncate font-semibold text-ink">{currentName}</span></div></div>
          <div className="flex items-center gap-3"><div className="hidden items-center gap-2 rounded-full border border-line bg-white px-3 py-1.5 text-[11px] text-slate-500 sm:flex"><span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />局域网 API</div><span className="hidden text-[10px] text-slate-400 md:inline">192.168.1.21</span><div className="flex h-8 w-8 items-center justify-center rounded-lg bg-moss text-xs font-bold text-white">{session?.name?.slice(0, 1) ?? 'L'}</div></div>
        </header>
        <main className="mx-auto max-w-[1440px] p-4 sm:p-6 lg:p-8">{children}</main>
      </div>
    </div>
  );
}

function Brand({ collapsed = false }: { collapsed?: boolean }) {
  return <div className={cn('flex h-[72px] items-center gap-3 border-b border-white/10 px-5', collapsed && 'justify-center px-3')}><img src="/elseyou-brand.png" alt={collapsed ? 'Elseyou' : ''} className="h-9 w-9 shrink-0 rounded-xl object-cover" />{!collapsed && <div><p className="text-sm font-bold tracking-tight">ELSEYOU</p><p className="font-mono text-[9px] uppercase tracking-[0.18em] text-white/40">control plane</p></div>}</div>;
}

function NavigationItem({ item, collapsed = false, onNavigate }: { item: NavigationEntry; collapsed?: boolean; onNavigate?: () => void }) {
  const Icon = item.icon;
  return <NavLink end={item.href === '/'} to={item.href} onClick={onNavigate} className={({ isActive }) => cn(
    'group flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-mint focus-visible:ring-offset-2 focus-visible:ring-offset-ink',
    isActive
      ? 'bg-mint font-semibold text-ink shadow-[0_5px_20px_rgba(201,232,220,0.12)] hover:bg-mint/90'
      : 'text-white/55 hover:bg-white/10 hover:text-white',
    collapsed && 'justify-center px-2',
  )} title={collapsed ? item.label : undefined}><Icon className="h-4 w-4 shrink-0" />{!collapsed && <span>{item.label}</span>}</NavLink>;
}
